<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";
import { useRouter } from "vue-router";

const router = useRouter();

const projects = [
  {
    id: 'P-2024-0031',
    name: 'Solar Park Beta – Northern Highlands',
    sector: 'Renewable Energy',
    country: 'Kenya',
    status: 'Active',
    statusColor: 'green',
    stage: 'Monitoring',
    creditsIssued: 6120,
    creditsAvailable: 5840,
    creditingPeriodEnd: '2029-03-31',
    lastActivity: '2025-04-15',
  },
  {
    id: 'P-2024-0042',
    name: 'Mangrove Restoration Initiative – Borneo',
    sector: 'Blue Carbon',
    country: 'Indonesia',
    status: 'Under Validation',
    statusColor: 'yellow',
    stage: 'DOE Validation',
    creditsIssued: 0,
    creditsAvailable: 0,
    creditingPeriodEnd: 'Pending',
    lastActivity: '2025-03-12',
  },
  {
    id: 'P-2023-0088',
    name: 'Forest Conservation Pilot – Amazon Basin',
    sector: 'Avoided Deforestation',
    country: 'Brazil',
    status: 'Draft',
    statusColor: 'gray',
    stage: 'PIN Draft',
    creditsIssued: 0,
    creditsAvailable: 0,
    creditingPeriodEnd: 'Pending',
    lastActivity: '2025-01-08',
  },
  {
    id: 'P-2022-0014',
    name: 'Wind Farm Alpha – Coastal Region',
    sector: 'Renewable Energy',
    country: 'Kenya',
    status: 'Completed',
    statusColor: 'blue',
    stage: 'Closed',
    creditsIssued: 45200,
    creditsAvailable: 0,
    creditingPeriodEnd: '2023-12-31',
    lastActivity: '2024-02-20',
  },
]

const statusClass = (color: string) => {
  const map: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    gray: 'bg-gray-100 text-gray-600',
    blue: 'bg-blue-100 text-blue-800',
    red: 'bg-red-100 text-red-800',
  }
  return map[color] ?? 'bg-gray-100 text-gray-600'
}

const stageIcon = (stage: string) => {
  const map: Record<string, string> = {
    'PIN Draft': 'fa-pencil-alt',
    'DOE Validation': 'fa-clipboard-check',
    'Monitoring': 'fa-chart-line',
    'Closed': 'fa-check-double',
  }
  return map[stage] ?? 'fa-circle'
}
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-6xl mx-auto px-5">

        <!-- Header -->
        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-3xl font-bold text-accent mb-2">My Projects</h1>
                <p class="text-gray-600">Manage all your registered and in-progress carbon projects.</p>
              </div>
              <button
                @click="router.push('/developer/project/submit-pcn')"
                class="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90 flex items-center"
              >
                <i class="fas fa-plus mr-2"></i>New Project
              </button>
            </div>
          </div>
        </section>

        <!-- Stats -->
        <div class="grid grid-cols-4 gap-4 mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-accent">4</p>
            <p class="text-sm text-gray-600">Total Projects</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-green-600">1</p>
            <p class="text-sm text-gray-600">Active</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-yellow-600">1</p>
            <p class="text-sm text-gray-600">In Progress</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-primary">51,320</p>
            <p class="text-sm text-gray-600">Total Credits Issued</p>
          </div>
        </div>

        <!-- Filter bar -->
        <div class="bg-white rounded-lg border border-gray-200 p-4 mb-6 flex items-center space-x-4">
          <div class="flex-1">
            <input
              type="text"
              class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Search projects..."
            />
          </div>
          <select class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
            <option>All Statuses</option>
            <option>Active</option>
            <option>Under Validation</option>
            <option>Draft</option>
            <option>Completed</option>
          </select>
          <select class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
            <option>All Sectors</option>
            <option>Renewable Energy</option>
            <option>Forestry</option>
            <option>Blue Carbon</option>
          </select>
        </div>

        <!-- Project list -->
        <section class="space-y-4">
          <div
            v-for="project in projects"
            :key="project.id"
            class="bg-white rounded-lg border border-gray-200 p-5 hover:border-primary/50 transition-colors cursor-pointer"
            @click="router.push(`/developer/project/${project.id}`)"
          >
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center space-x-3 mb-1">
                  <h3 class="font-semibold text-accent">{{ project.name }}</h3>
                  <span :class="['inline-flex px-2 py-1 rounded-full text-xs font-medium', statusClass(project.statusColor)]">
                    {{ project.status }}
                  </span>
                </div>
                <p class="text-sm text-gray-500 mb-3">
                  {{ project.id }} &nbsp;·&nbsp; {{ project.sector }} &nbsp;·&nbsp; {{ project.country }}
                </p>
                <div class="flex items-center space-x-6 text-sm">
                  <span class="flex items-center text-gray-600">
                    <i :class="['fas mr-1.5 text-primary', stageIcon(project.stage)]"></i>
                    Stage: <span class="font-medium ml-1 text-gray-800">{{ project.stage }}</span>
                  </span>
                  <span class="flex items-center text-gray-600">
                    <i class="fas fa-leaf mr-1.5 text-primary"></i>
                    Credits Issued: <span class="font-medium ml-1 text-gray-800">{{ project.creditsIssued.toLocaleString() }} tCO2e</span>
                  </span>
                  <span class="flex items-center text-gray-600">
                    <i class="fas fa-calendar-alt mr-1.5 text-primary"></i>
                    Crediting Period End: <span class="font-medium ml-1 text-gray-800">{{ project.creditingPeriodEnd }}</span>
                  </span>
                </div>
              </div>
              <div class="text-right text-sm ml-4">
                <p class="text-gray-500">Last activity</p>
                <p class="font-medium text-gray-700">{{ project.lastActivity }}</p>
                <i class="fas fa-chevron-right text-gray-400 mt-2"></i>
              </div>
            </div>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>
